import { useGT } from 'gt-react';
import { ColumnConfigDialog } from '@/components/common/ColumnConfigDialog';
import {
  activeLevels,
  activeSortLimit,
  sortOptions,
  withInactiveLevels,
} from '@/components/common/columnConfigSorting';
import { CALC_COLUMN_ID, QUANTITY_COLUMN_ID } from '../utils/buyerBoardColumns';
import type { BuyerBoardColumnConfig } from './useBuyerBoardColumnConfig';

/**
 * The column setup for a buyer board table: choose, hide, reorder, rename,
 * align and bold columns, and (line table only) the ordered sort, saved as the buyer's
 * view (or, for an admin, as the org default). The Orders table sorts on the
 * server by its header, so it has no saved sort.
 */
export function BuyerBoardColumnsDialog({
  open,
  onOpenChange,
  config,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: BuyerBoardColumnConfig;
}) {
  const gt = useGT();
  const hasSort = config.table === 'lines';
  const sortableIds = new Set(
    sortOptions(config.columns).map((option) => option.value),
  );
  // Qty overrides Calc, so a drag moves the two together.
  const columnNotes = hasSort
    ? {
        [CALC_COLUMN_ID]: gt('Always right before Qty', {
          context:
            'buyer board column dialog note on the Calc column, which moves with Qty',
        }),
        [QUANTITY_COLUMN_ID]: gt('Overrides Calc, moves with it', {
          context:
            'buyer board column dialog note on the Qty column, which is paired with Calc',
        }),
      }
    : undefined;
  return (
    <ColumnConfigDialog
      open={open}
      onOpenChange={onOpenChange}
      columns={config.columns}
      fields={[]}
      onUpdate={config.updateColumn}
      onReorder={config.moveColumn}
      onReset={config.reset}
      onSaveAsOrgDefault={config.saveAsOrgDefault}
      showFilterToggle={false}
      showSortToggle={hasSort}
      showBoldToggle
      sorting={hasSort ? activeLevels(config.sorting, sortableIds) : undefined}
      onSortingChange={
        hasSort
          ? (next) =>
              config.setSorting(
                withInactiveLevels(next, config.sorting, sortableIds),
              )
          : undefined
      }
      maxSortLevels={activeSortLimit(config.sorting, sortableIds)}
      viewSource={config.viewSource}
      columnNotes={columnNotes}
    />
  );
}
